import { useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { IconArrowDown } from '@components/icons/arrow-down';
import { IconOpen } from '@components/icons/open';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { useSwitchCompany } from 'features/authentication';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';
import { IAuthenticatedUserCompany } from 'types/authentication';

type MoveApplicationModalProps = ModalProps<{
  name: string;
  moved: boolean;
  newJobId: number;
  oldJobTitle: string;
  newJobTitle: string;
  modelCompanyId: number;
  currentApplicationId: number;
  newApplicationIdCopied: number;
  currentApplicationJobId: number;
  model: { company: IAuthenticatedUserCompany };
}>;

function MoveApplicationModal({
  name,
  moved,
  model,
  newJobId,
  closeModal,
  oldJobTitle,
  newJobTitle,
  modelCompanyId,
  currentApplicationId,
  newApplicationIdCopied,
  currentApplicationJobId,
}: MoveApplicationModalProps) {
  const applicationId = newApplicationIdCopied ? newApplicationIdCopied : currentApplicationId;
  const { current, t } = useStore();
  const navigate = useNavigate();
  const toPath = `/jobs/${newJobId}/application/${applicationId}`;
  const switchCompany = useSwitchCompany(toPath);
  const follow = () => {
    if (current.id !== modelCompanyId) {
      switchCompany.mutate(model.company);
    } else {
      navigate(toPath);
    }
  };

  const back = () => {
    if (moved) {
      navigate(`/jobs/${currentApplicationJobId}`);
    } else {
      navigate(`/jobs/${currentApplicationJobId}/application/${currentApplicationId}`);
    }
  };

  return (
    <Modal>
      <MoveContainer>
        <Stack space={5} orientation="vertical">
          <Modal.Header
            text={
              moved
                ? t('application.movecopy.modal.move.title')
                : t('application.movecopy.modal.copy.title')
            }
          />
          <div>
            <Text font="semibold14" text={t('application.movecopy.modal.label.application')} />
            <Text text={name} color="purple100" />
          </div>
          <div>
            <Text font="semibold14" text={t('application.movecopy.modal.label.job')} />
            <Text text={oldJobTitle} color="red200" />
            <IconArrowDown color="purple50" />
            <Text text={newJobTitle} color="green200" />
          </div>
        </Stack>
      </MoveContainer>
      <Modal.Actions>
        <Stack space={4} orientation="horizontal" align="center">
          <Button
            variant="secondary"
            text={t('application.movecopy.modal.back')}
            onClick={() => {
              back();
              closeModal();
            }}
          />
          <Button
            variant="secondary"
            iconLeft={<IconOpen />}
            text={
              moved
                ? t('application.movecopy.modal.follow')
                : t('application.movecopy.modal.follow.copy')
            }
            onClick={() => {
              follow();
              closeModal();
            }}
          />
        </Stack>
      </Modal.Actions>
    </Modal>
  );
}

export const MoveContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
`;

export default observer(MoveApplicationModal);
